import type {PropsWithChildren} from 'react'
import {createContext, use, useCallback, useMemo, useState} from 'react'

import type {ThreadSummary} from '../../commit-conversations/types'
import {ensurePreviousActiveDialogIsClosed} from '../../commit-conversations/util/ensure-previous-active-dialog-is-closed'
import {threadSummary} from '../helpers/conversation-helpers'
import {notEmpty} from '../helpers/not-empty'
import type {CommentAuthor, DiffAnnotation, DiffLine, DiffSide} from '../types'
import {useDiffContext} from './DiffContext'
import {useDiffLineContext} from './DiffLineContext'

const THREAD = 'thread'
const ANNOTATION = 'annotation'
type SelectedMarker = {
  id: string
  kind: typeof THREAD | typeof ANNOTATION
}

type DialogType = 'new-conversation' | 'marker-details' | 'marker-list' | 'context-menu'

type OpenDialogState = {
  type: DialogType | null
  cellId?: string
  withSuggestedChange?: boolean
}

const CLOSED_DIALOG: OpenDialogState = {type: null}

type MarkersDialogActions = {
  showActionBar: () => void
  hideActionBar: () => void
  openDialog: (dialog: DialogType, cellId?: string | undefined, opts?: {withSuggestedChange?: boolean}) => void
  closeDialog: (dialog: DialogType) => void
  selectThreadId: (threadId?: string) => void
  selectAnnotationId: (annotationId?: string) => void
  updateVisibleThreads: (diffSide?: DiffSide) => ThreadSummary[] | undefined
}

type MarkersDialogState = {
  annotations: DiffAnnotation[]
  threads: ThreadSummary[]
  anyMenuOpen: boolean
  selectedThreadId?: string
  selectedAnnotationId?: string
  openContextMenuCell?: string
  openMarkersListCell?: string
  openMarkerDetailsCell?: string
  openNewConversationCell?: {cellId: string | undefined; withSuggestedChange?: boolean}
}

// Split into three contexts so a consumer only re-renders for the slice it reads:
// - Actions: stable callbacks that never change identity
// - ActionBarVisibility: high-frequency hover state, isolated from dialog state
// - DialogState: click-driven dialog/selection state plus per-line marker data
const MarkersDialogActionsContext = createContext<MarkersDialogActions | null>(null)
const MarkersActionBarVisibilityContext = createContext<boolean>(false)
const MarkersDialogStateContext = createContext<MarkersDialogState | null>(null)

/**
 * Manages the state of thread, comment, and annotation dialogs for a line in a diff
 */
export function MarkersDialogContextProvider({children, line}: PropsWithChildren<{line: DiffLine}>) {
  // Tracks the state of the action bar visibility for this row
  const [isActionBarVisible, setIsActionBarVisible] = useState(false)
  // Tracks which dialog (if any) is open on this line. The four dialog types are
  // mutually exclusive, so they are modeled as a single state machine rather than
  // four independent booleans.
  const [openDialogState, setOpenDialogState] = useState<OpenDialogState>(CLOSED_DIALOG)

  // The selected "marker" that represents what entity is open in a dialog on a line
  const [selectedMarker, setSelectedMarker] = useState<SelectedMarker>()

  const {ghostUser, markerNavigationImplementation} = useDiffContext()
  // Information about what other threads exist on that line
  const {originalLineThreads, modifiedLineThreads} = useDiffLineContext()
  const [sideWithOpenThreads, setSideWithOpenThreads] = useState<DiffSide>()
  const threads = useMemo(
    () =>
      getThreadsForSide({
        line,
        side: sideWithOpenThreads,
        originalLineThreads,
        modifiedLineThreads,
        ghostUser,
      }),
    [line, sideWithOpenThreads, originalLineThreads, modifiedLineThreads, ghostUser],
  )

  const openContextMenuCell = openDialogState.type === 'context-menu' ? openDialogState.cellId : undefined
  const openMarkersListCell = openDialogState.type === 'marker-list' ? openDialogState.cellId : undefined
  const openMarkerDetailsCell = openDialogState.type === 'marker-details' ? openDialogState.cellId : undefined
  const openNewConversationCell = useMemo(
    () =>
      openDialogState.type === 'new-conversation'
        ? {cellId: openDialogState.cellId, withSuggestedChange: openDialogState.withSuggestedChange}
        : undefined,
    [openDialogState],
  )
  const anyMenuOpen = openDialogState.type !== null

  const showActionBar = useCallback(() => {
    setIsActionBarVisible(true)
  }, [])

  const hideActionBar = useCallback(() => {
    setIsActionBarVisible(false)
  }, [])

  const selectThreadId = useCallback((threadId?: string) => {
    if (threadId) {
      setSelectedMarker({id: threadId, kind: THREAD})
    } else {
      setSelectedMarker(undefined)
    }
  }, [])

  const selectedThreadId = selectedMarker?.kind === THREAD ? selectedMarker.id : undefined

  const selectedAnnotationId = selectedMarker?.kind === ANNOTATION ? selectedMarker.id : undefined
  const selectAnnotationId = useCallback((annotationId?: string) => {
    if (annotationId) {
      setSelectedMarker({id: annotationId, kind: ANNOTATION})
    } else {
      setSelectedMarker(undefined)
    }
  }, [])

  const openDialog = useCallback(
    (dialog: DialogType, cellId?: string, opts?: {withSuggestedChange?: boolean}) => {
      // We currently allow for Dialogs to be open while a user moves and clicks around the diff now.
      // This ensures we close active dialogs before opening up a new dialog
      ensurePreviousActiveDialogIsClosed(markerNavigationImplementation?.overlayPortalContainerName)
      setOpenDialogState({type: dialog, cellId, withSuggestedChange: opts?.withSuggestedChange})
    },
    [markerNavigationImplementation?.overlayPortalContainerName],
  )

  const closeDialog = useCallback((dialog: DialogType) => {
    setOpenDialogState(prev => (prev.type === dialog ? CLOSED_DIALOG : prev))
  }, [])

  const updateVisibleThreads = useCallback(
    (diffSide?: DiffSide) => {
      setSideWithOpenThreads(diffSide)
      return getThreadsForSide({
        line,
        side: diffSide,
        modifiedLineThreads,
        originalLineThreads,
        ghostUser,
      })
    },
    [line, modifiedLineThreads, originalLineThreads, ghostUser],
  )

  const annotations = useMemo(
    () => line.annotationsData?.annotations?.filter(notEmpty) ?? [],
    [line.annotationsData?.annotations],
  )

  const actions = useMemo<MarkersDialogActions>(
    () => ({
      showActionBar,
      hideActionBar,
      openDialog,
      closeDialog,
      selectThreadId,
      selectAnnotationId,
      updateVisibleThreads,
    }),
    [showActionBar, hideActionBar, openDialog, closeDialog, selectThreadId, selectAnnotationId, updateVisibleThreads],
  )

  const dialogState = useMemo<MarkersDialogState>(
    () => ({
      annotations,
      threads,
      anyMenuOpen,
      selectedThreadId,
      selectedAnnotationId,
      openContextMenuCell,
      openMarkersListCell,
      openMarkerDetailsCell,
      openNewConversationCell,
    }),
    [
      annotations,
      threads,
      anyMenuOpen,
      selectedThreadId,
      selectedAnnotationId,
      openContextMenuCell,
      openMarkersListCell,
      openMarkerDetailsCell,
      openNewConversationCell,
    ],
  )

  return (
    <MarkersDialogActionsContext value={actions}>
      <MarkersActionBarVisibilityContext value={isActionBarVisible}>
        <MarkersDialogStateContext value={dialogState}>{children}</MarkersDialogStateContext>
      </MarkersActionBarVisibilityContext>
    </MarkersDialogActionsContext>
  )
}

/**
 * Stable action callbacks for managing the dialogs on a line in a diff. Consumers
 * that only trigger actions will not re-render when dialog or hover state changes.
 */
export function useMarkersDialogActions(): MarkersDialogActions {
  const actions = use(MarkersDialogActionsContext)
  if (!actions) {
    throw new Error('useMarkersDialogActions must be used within a MarkersDialogContextProvider')
  }

  return actions
}

/**
 * Whether the action bar is visible for this line. Isolated from dialog state so
 * the high-frequency hover/focus updates only re-render the cells that show it.
 */
export function useActionBarVisibility(): boolean {
  return use(MarkersActionBarVisibilityContext)
}

/**
 * Click-driven dialog/selection state plus per-line marker data (annotations and
 * threads) for managing the dialog states on a line in a diff.
 */
export function useMarkersDialogState(): MarkersDialogState {
  const dialogState = use(MarkersDialogStateContext)
  if (!dialogState) {
    throw new Error('useMarkersDialogState must be used within a MarkersDialogContextProvider')
  }

  return dialogState
}

function getThreadsForSide({
  line,
  side,
  originalLineThreads,
  modifiedLineThreads,
  ghostUser,
}: {
  line: DiffLine
  side?: DiffSide
  originalLineThreads?: ThreadSummary[]
  modifiedLineThreads?: ThreadSummary[]
  ghostUser?: CommentAuthor
}): ThreadSummary[] {
  switch (side) {
    case 'LEFT':
      return originalLineThreads || []
    case 'RIGHT':
      return modifiedLineThreads || []
    case undefined:
      return threadSummary(line.threadsData, ghostUser)
    default:
      return []
  }
}
